import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetTrigger } from "src/components/ui/sheet";
import {
  ChevronDown,
  ChevronUp,
  Menu,
} from "lucide-react";
import { Button } from "src/components/ui/button";
import getUserId from "src/utils/getUserId";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useToast } from "src/hooks/use-toast";

const SideMenuMobile = () => {
  const userId = getUserId();
  const { t } = useTranslation();
  const router = useRouter();
  const { toast } = useToast();

  const [openDropdown, setOpenDropdown] = useState<string>("");

  const handleToggleDropdown = (dropdown: any) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const handleProtectedAction = (e: React.MouseEvent) => {
    if (!userId) {
      e.preventDefault();
      toast({
        variant: "destructive",
        description: "Please login to access this feature",
      });
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    router.replace("/");
  };

  return (
    <Sheet>
      <SheetTrigger>
        <Menu className="size-5 dark:text-white md:hidden" />
      </SheetTrigger>
      <SheetContent side="left" className="space-y-2 p-0">
        {/* <Categories /> */}
        <Link
          href="/settings/account/my-profile"
          className="block"
          onClick={handleProtectedAction}
        >
          <div className="flex h-12 w-full items-center justify-between border-b px-3 text-black dark:text-white">
            <p>Profile</p>
          </div>
        </Link>
        <div
          className="h-auto cursor-pointer border-b px-3 text-black dark:text-white"
          onClick={(e) => {
            if (!userId) {
              handleProtectedAction(e);
            } else {
              handleToggleDropdown("orders");
            }
          }}
        >
          <div className="flex h-12 items-center justify-between">
            <p>My Order</p>
            {openDropdown === "orders" ? (
              <ChevronUp className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
          </div>
          {openDropdown === "orders" && userId && (
            <div className="flex flex-col items-start justify-start gap-y-2 px-5 pb-3 text-sm text-black text-opacity-70 dark:text-white">
              <Link
                href={`/settings/account/my-orders?_id=${userId}&section=brought&status=All`}
              >
                - Brought
              </Link>
              <Link
                href={`/settings/account/my-orders?_id=${userId}&section=sold&status=All`}
              >
                - Sold
              </Link>
            </div>
          )}
        </div>

        <div
          className="h-auto cursor-pointer border-b px-3 text-black dark:text-white"
          onClick={(e) => {
            if (!userId) {
              handleProtectedAction(e);
            } else {
              handleToggleDropdown("wallet");
            }
          }}
        >
          <div className="flex h-12 items-center justify-between">
            <p>My Wallet</p>
            {openDropdown === "wallet" ? (
              <ChevronUp className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
          </div>
          {openDropdown === "wallet" && userId && (
            <div className="flex flex-col items-start justify-start gap-y-2 px-5 pb-3 text-sm text-black text-opacity-70 dark:text-white">
              <Link href="/settings/account/my-wallet/wallet">- Walllet</Link>
              <Link href="/settings/account/my-wallet/payment-history">
                - Payment History
              </Link>
            </div>
          )}
        </div>

        <Link
          href="/settings/account/my-ads"
          className="block dark:text-white"
          onClick={handleProtectedAction}
        >
          <div className="flex h-12 w-full items-center justify-between border-b bg-transparent px-3 text-black dark:text-white">
            <p>My Ads</p>
          </div>
        </Link>

        <div
          className="h-auto cursor-pointer border-b px-3 text-black dark:text-white"
          onClick={(e) => {
            if (!userId) {
              handleProtectedAction(e);
            } else {
              handleToggleDropdown("listing");
            }
          }}
        >
          <div className="flex h-12 items-center justify-between">
            <p>My Listing</p>
            {openDropdown === "listing" ? (
              <ChevronUp className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
          </div>
          {openDropdown === "listing" && userId && (
            <div className="flex flex-col items-start justify-start gap-y-2 px-5 pb-3 text-sm text-black text-opacity-70 dark:text-white">
              <Link href="/settings/account/my-listing/item-lists">
                - Item Lists
              </Link>
              <Link href="/settings/account/my-listing/reviews">- Reviews</Link>
            </div>
          )}
        </div>

        <div
          className="cursor-pointer border-b px-3 text-black dark:text-white"
          onClick={(e) => {
            if (!userId) {
              handleProtectedAction(e);
            } else {
              handleToggleDropdown("accountSettings");
            }
          }}
        >
          <div className="flex h-12 items-center justify-between">
            <p>Account Settings</p>
            {openDropdown === "accountSettings" ? (
              <ChevronUp className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
          </div>
          {openDropdown === "accountSettings" && userId && (
            <div className="flex flex-col items-start justify-start gap-y-2 px-5 pb-3 text-sm text-black text-opacity-70 dark:text-white">
              <Link href="/settings/account-settings/email-phone-verification">
                - Email & Phone No Verification
              </Link>
              <Link href="/settings/account-settings/holiday-mode">
                - Holiday Mode
              </Link>
              <Link href="/settings/account-settings/account-deletion">
                - Account Deletion
              </Link>
            </div>
          )}
        </div>

        <Link
          href="/settings/account/personalization"
          className="block"
          onClick={handleProtectedAction}
        >
          <div className="flex h-12 w-full items-center justify-between border-b bg-transparent px-3 text-black dark:text-white">
            <p>Personalization</p>
          </div>
        </Link>

        <Link
          href="/settings/account/my-address"
          className="block"
          onClick={handleProtectedAction}
        >
          <div className="flex h-12 w-full items-center justify-between border-b bg-transparent px-3 text-black dark:text-white">
            <p>My Addresses</p>
          </div>
        </Link>

        <div
          className="h-auto cursor-pointer border-b px-3 text-black"
          onClick={(e) => {
            if (!userId) {
              handleProtectedAction(e);
            } else {
              handleToggleDropdown("settings");
            }
          }}
        >
          <div className="flex h-12 items-center justify-between dark:text-white">
            <p>Settings</p>
            {openDropdown === "settings" ? (
              <ChevronUp className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
          </div>
          {openDropdown === "settings" && userId && (
            <div className="flex flex-col items-start justify-start gap-y-2 px-5 pb-3 text-sm text-black text-opacity-70 dark:text-white">
              <Link href="/settings/other-settings/change-password">
                - Change Password
              </Link>
              <Link href="/settings/other-settings/dark-mode">- Dark Mode</Link>
              <Link href="/settings/other-settings/bundle-discount">
                - Bundle Discount
              </Link>
            </div>
          )}
        </div>

        {userId ? (
          <Button
            variant="link"
            className="mt-10 text-lg font-medium text-primary"
            onClick={handleLogout}
          >
            Logout
          </Button>
        ) : (
          <Link href="/auth/login">
            <Button
              variant="link"
              className="mt-10 text-lg font-medium text-primary"
            >
              Login
            </Button>
          </Link>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default SideMenuMobile;
